import React, { useEffect } from "react";
import "react-toastify/dist/ReactToastify.css";
import DataTable, { createTheme } from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../../context/ThemeContext";
import TableLoader from "../../skeletons/TableLoader";
import { checkPermission } from "../../../helpers/GeneralHelper";
import { utcToLocal } from "../../../helpers/DateHelper";
import { getCmsDataTableStyles } from "../../../helpers/DataTableStyles";

function rowIsActive(row) {
  return (
    row.user_is_active == 1 ||
    row.user_is_active === "1" ||
    row.user_is_active === true ||
    String(row.user_is_active || "").toLowerCase() === "active"
  );
}

export default function UserTable({
  module,
  data,
  loading,
  totalRows,
  page,
  limit,
  fetchData,
  openUpdateModal,
  openDeleteModal,
  handleView,
  isTrash,
  statusFilter = "active",
  canViewSessionLogs = false,
  sessionLogsManagerId = null,
}) {
  const navigate = useNavigate();

  const columns = [
    {
      name: "#",
      selector: (row, index) => <div>{ ((page -1 ) * limit) + index +1 }.</div>,
      width: '100px'
    },
    {
      name: "Name",
      selector: (row) => row.user_name,
      sortable: true, // ✅ enable sorting
    },
    {
      name: "Mobile",
      selector: (row) => row.user_mobile,
      sortable: true, // ✅ enable sorting
      width: "168px",
      minWidth: "168px",
      maxWidth: "185px",
      cell: (row) => (
        <span className="text-nowrap" style={{ whiteSpace: "nowrap" }}>
          {row.user_mobile ?? "—"}
        </span>
      ),
    },
    {
      name: "Email",
      selector: (row) => row.user_email,
      sortable: true, // ✅ enable sorting
      minWidth: "260px",
      width: "280px",
    },
    {
      name: "Role",
      selector: (row) => row.urole_title || "",
      sortable: true, // ✅ enable sorting
      width: "148px",
      minWidth: "148px",
      maxWidth: "155px",
      cell: (row) => {
        const text = row.urole_title ?? "";
        return (
          <div
            className="text-truncate"
            title={text}
            style={{ minWidth: 0, width: "100%" }}
          >
            {text || "—"}
          </div>
        );
      },
    },
    {
      name: "Designation",
      selector: (row) => row.designation_name,
      sortable: true, // ✅ enable sorting
    },
    {
      name: "Manager",
      selector: (row) => row.manager_name,
      sortable: true, // ✅ enable sorting
    },
    ...(!isTrash
      ? [
          {
            name: "Status",
            width: "108px",
            minWidth: "108px",
            maxWidth: "108px",
            sortable: false,
            cell: (row) =>
              rowIsActive(row) ? (
                <span className="badge bg-success text-white fw-semibold rounded-pill px-2 py-1 small">
                  Active
                </span>
              ) : (
                <span className="badge bg-secondary text-white fw-semibold rounded-pill px-2 py-1 small">
                  Inactive
                </span>
              ),
          },
        ]
      : []),
    // {
    //   name: "Added On",
    //   selector: (row) => utcToLocal(row.user_create_time)
    // }
  ];

  const showActionColumn =
    !isTrash &&
    (canViewSessionLogs ||
      checkPermission(module.route, "edit") ||
      checkPermission(module.route, "delete"));

  if (showActionColumn) {
    columns.push({
      name: "Action",
      width: canViewSessionLogs ? "124px" : "88px",
      minWidth: canViewSessionLogs ? "124px" : "88px",
      maxWidth: canViewSessionLogs ? "132px" : "88px",
      sortable: false,
      cell: (row) => (
        <div className="hstack gap-1 justify-content-center align-items-center flex-nowrap">
          {canViewSessionLogs &&
            row._user_id &&
            (!sessionLogsManagerId ||
              String(row.user_manager_id) === String(sessionLogsManagerId)) && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-primary-light"
              title="Login activity"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/users/session-logs?user_id=${row._user_id}`, {
                  state: { memberName: row.user_name || "" },
                });
              }}
            >
              <i className="ri-login-circle-line"></i>
            </button>
          )}
          {checkPermission(module.route, "edit") && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-info"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                openUpdateModal(row);
              }}
            >
              <i className="bx bxs-edit"></i>
            </button>
          )}
          {checkPermission(module.route, "delete") && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-danger"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                openDeleteModal(row);
              }}
            >
              <i className="bx bxs-trash"></i>
            </button>
          )}
        </div>
      ),
    });
  }

  if (isTrash) {
    columns.push({
      name: "Deleted At",
      selector: (row) => utcToLocal(row.user_delete_time)
    });
  }

  const { theme, toggleTheme } = useTheme();

  const handlePageChange = async (page) => {
    fetchData(limit, page);
  };

  const handleLimitChange = async (newLimit, page) => {
    //if ((newLimit !== 10 /*&& page !== 1*/) || localStorage.getItem(module.key)) {
      fetchData(newLimit, page);
    //}
  };

  useEffect(() => {
    fetchData(limit, 1);
  }, [isTrash, statusFilter]);

  createTheme("dark", {
    text: {
      primary: "#a2a3a4",
      secondary: "#2aa198",
    },
    background: {
      default: "#252729",
    },
    context: {
      background: "#cb4b16",
      text: "#a2a3a4",
    },
    divider: {
      default: "#073642",
    },
    action: {
      button: "rgba(0,0,0,.54)",
      hover: "rgba(0,0,0,.08)",
      disabled: "rgba(0,0,0,.12)",
    },
  });

  return (
    <DataTable
      // title={`Manage Notes (Private)`}
      className="clickable cms-data-table"
      onRowClicked={(row) => handleView(row)}
      columns={columns}
      data={data}
      pagination
      paginationServer
      paginationTotalRows={totalRows}
      onChangeRowsPerPage={handleLimitChange}
      onChangePage={handlePageChange}
      progressPending={loading}
      progressComponent={<TableLoader noOfColumn={columns.length} />}
      fixedHeader
      paginationPerPage={10}  // default page size
      paginationRowsPerPageOptions={[10, 25, 50, 100, totalRows]} // 👈 added custom options
      fixedHeaderScrollHeight="550px"
      highlightOnHover
      theme={`${theme === "light" ? "light" : "dark"}`}
      customStyles={getCmsDataTableStyles(theme)}
    />
  );
};
